import styled from 'styled-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from '@material-ui/core';
import { Visibility as VisibilityIcon } from '@material-ui/icons';
import { map, truncate } from 'lodash-es';
import { ChannelEvent, DeliveryLogType, MessageCategory } from '@dev/ee-api/ee-api-models';
import { convertToLocalTime } from '@dev/ee-tools';
import {
    BasicTableRowsProps,
    ContentLoader,
    CustomTableRowsProps,
    MobileCardRowsProps,
    palette,
    Typography,
} from '@dev/ee-components';

import DeliveryLogBadge from '@/components/DeliveryLogBadge';
import useMobile from '@/services/hooks/useMobile';
import { ApplicationState } from '@/store';
import { ResendStart } from '@/store/reports/actions';
import { SetEmailMsgid } from '@/store/emails/actions';
import { SetPickedContactEmail } from '@/store/campaigns/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { links } from '@/services/dictionaries/links';
import { isReseller } from '@/services/Secuirty';

import { isResendAllowed, prepareErrorMessageTitle } from '../../../helperFunctions';

const LogDateWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: baseline;

    .MuiTypography-body1 {
        margin-right: 5px;
        color: ${palette.gray[7]};
    }
`;

const DateWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-right: -8px;
`;

const LogsError = styled.div<{ clickable?: boolean }>`
    cursor: pointer;
    ${({ clickable }) =>
        clickable &&
        `
    text-decoration: underline;
    `}
    &:hover {
        text-decoration: underline;
    }
`;

const ContactEmailWrapper = styled.span`
    &:hover {
        text-decoration: underline;
        cursor: pointer;
    }
`;

const VisiblilityIconWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    &:hover {
        cursor: pointer;
        position: relative;
        &::before {
            content: '';
            position: absolute;
            z-index: 2;
            width: 32px;
            height: 32px;
            right: -2px;
            background-color: ${palette.indigo[1]};
            border-radius: 20px;
        }
    }
    & svg {
        font-size: 28px;
        z-index: 2;
        fill: #718096;
    }
`;

const HelpFont = styled(Typography)`
    font-size: 11px;
`;

const useLoadingRows = (): CustomTableRowsProps[] => {
    const isMobile = useMobile();
    const { reports } = useSelector((state: ApplicationState) => state);
    const { eventLogParamsByChannelName } = reports;
    const perPage = eventLogParamsByChannelName?.limit || 5;
    const rows: CustomTableRowsProps[] = [];
    let index;

    for (index = 0; index < perPage; index++) {
        rows.push({
            to: <ContentLoader mode="row" height={20} />,
            logsDate: <ContentLoader mode="row" height={20} />,
            ...(!isMobile && {
                emptyColumn: '',
            }),
            event: <ContentLoader mode="row" height={20} />,
            bounce: <ContentLoader mode="row" height={20} />,
            ...(!isMobile && {
                icon: <ContentLoader mode="row" height={20} />,
            }),
        });
    }

    return rows;
};

const formatDate = (date): JSX.Element => {
    const days = convertToLocalTime(date, 'L');
    const hours = convertToLocalTime(date, 'LT');

    return (
        <DateWrapper>
            <Typography variant="caption">{days}</Typography>
            <HelpFont variant="body1" bodyVariant="help">
                {hours}
            </HelpFont>
        </DateWrapper>
    );
};

export interface BounceCellInterface {
    recipient: ChannelEvent;
    openLogErrorModal: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, channelEvent: ChannelEvent) => void;
    logErrorTitle: string;
    mobileUnknownMessage?: string;
}

const BounceCell: FC<BounceCellInterface> = (props: BounceCellInterface): JSX.Element => {
    const { logErrorTitle, mobileUnknownMessage, openLogErrorModal, recipient } = props;
    const isMobile = useMobile();

    const mobileError = (
        <LogsError clickable={!!recipient.Message}>
            <Typography variant="body1" bodyVariant="onPaper">
                {logErrorTitle || mobileUnknownMessage}
            </Typography>
        </LogsError>
    );

    const error = recipient.Message ? (
        <LogsError>
            <Typography variant="caption">{recipient.BounceType}</Typography>
            <HelpFont variant="body1" bodyVariant="help">
                {logErrorTitle}
            </HelpFont>
        </LogsError>
    ) : (
        <>
            <Typography variant="caption">{recipient.BounceType}</Typography>
            <HelpFont variant="body1" bodyVariant="help">
                {logErrorTitle}
            </HelpFont>
        </>
    );

    return (
        <div
            onClick={(e): void => {
                if (recipient.Message) {
                    openLogErrorModal(e, recipient);
                }
            }}
        >
            {isMobile ? mobileError : error}
        </div>
    );
};

export const useTableRows = (): BasicTableRowsProps[] | MobileCardRowsProps[] => {
    const { reports } = useSelector((state: ApplicationState) => state);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const loadingRows = useLoadingRows();

    if (!reports?.eventLogByChannelName && !reports?.eventLogByChannelNameLoading) {
        return [];
    }

    if (reports?.eventLogByChannelNameLoading) {
        return loadingRows;
    }

    const openLogErrorModal = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, channelEvent: ChannelEvent): void => {
        e.stopPropagation();

        const logErrorTitle = prepareErrorMessageTitle(channelEvent);
        const logErrorText = channelEvent.Message;

        const link = (
            <Link target="_blank" style={{ color: palette.indigo[6] }} href={links.helpBounceErrorCategoriesAndFilters}>
                {t('learnmore')}
            </Link>
        );

        dispatch(
            ShowDialog({
                title: logErrorTitle,
                body: (
                    <span>
                        {logErrorText} {!isReseller() && link}
                    </span>
                ),
                cancelLabel: t('cancel'),
                ...(isResendAllowed(channelEvent)
                    ? {
                          confirmLabel: t('resend'),
                          onClickConfirm: () => {
                              dispatch(
                                  ResendStart({
                                      messageID: channelEvent.MessageID,
                                  })
                              );
                          },
                      }
                    : {}),
            })
        );
    };

    return map(reports?.eventLogByChannelName, (recipient, index) => {
        const isWaitingToRetry = recipient.EventType === DeliveryLogType.WaitingToRetry;
        const date = isWaitingToRetry ? recipient.NextTryOn : recipient.EventDate;
        const logErrorTitle = prepareErrorMessageTitle(recipient);
        const isScheduledFor = isWaitingToRetry ? (
            <HelpFont variant="body1" bodyVariant="help">
                {t('scheduledfor')}
            </HelpFont>
        ) : (
            ''
        );
        const mobileUnknownMessage = recipient.MessageCategory === MessageCategory.Unknown && t('unknown');
        const eventBadge = <DeliveryLogBadge status={recipient?.EventType} id={index} />;
        const logsDate = (
            <LogDateWrapper>
                {isScheduledFor}
                {formatDate(date)}
            </LogDateWrapper>
        );

        if (isMobile) {
            return {
                id: recipient.MessageID,
                to: recipient?.To,
                event: eventBadge,
                logsDate,
                ...((!!logErrorTitle || !!mobileUnknownMessage) && {
                    bounce: (
                        <BounceCell
                            recipient={recipient}
                            openLogErrorModal={openLogErrorModal}
                            logErrorTitle={logErrorTitle}
                            mobileUnknownMessage={mobileUnknownMessage}
                        />
                    ),
                }),
            };
        }

        return {
            to: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                    <ContactEmailWrapper
                        onClick={() => {
                            dispatch(SetPickedContactEmail(recipient?.To));
                        }}
                    >
                        {truncate(recipient?.To, {
                            length: 66,
                        })}
                    </ContactEmailWrapper>
                </div>
            ),
            logsDate,
            emptyColumn: '',
            event: eventBadge,
            bounce: (
                <BounceCell recipient={recipient} openLogErrorModal={openLogErrorModal} logErrorTitle={logErrorTitle} />
            ),
            icon: (
                <VisiblilityIconWrapper>
                    <VisibilityIcon
                        onClick={() => {
                            dispatch(SetEmailMsgid(recipient.MessageID));
                        }}
                    />
                </VisiblilityIconWrapper>
            ),
        };
    });
};
